import { CodeTabs } from './project-code-block';

interface Installer {
	label: string;
	icon: string;
	command: (pkg: string) => string;
}

const INSTALLERS: Record<string, Installer> = {
	// npm ecosystem
	npm: {
		label: 'npm',
		icon: 'catppuccin:npm',
		command: (p) => `npm install ${p}`,
	},
	pnpm: {
		label: 'pnpm',
		icon: 'catppuccin:pnpm',
		command: (p) => `pnpm add ${p}`,
	},
	bun: { label: 'bun', icon: 'catppuccin:bun', command: (p) => `bun add ${p}` },
	yarn: {
		label: 'yarn',
		icon: 'catppuccin:yarn',
		command: (p) => `yarn add ${p}`,
	},
	npx: { label: 'npx', icon: 'catppuccin:npm', command: (p) => `npx ${p}` },

	// Linux package managers
	pacman: {
		label: 'pacman',
		icon: 'logos:archlinux',
		command: (p) => `sudo pacman -S ${p}`,
	},
	apt: {
		label: 'apt',
		icon: 'logos:ubuntu',
		command: (p) => `sudo apt install ${p}`,
	},
	dnf: {
		label: 'dnf',
		icon: 'logos:fedora',
		command: (p) => `sudo dnf install ${p}`,
	},
	zypper: {
		label: 'zypper',
		icon: 'devicon:opensuse',
		command: (p) => `sudo zypper install ${p}`,
	},
	yay: {
		label: 'yay (AUR)',
		icon: 'logos:archlinux',
		command: (p) => `yay -S ${p}`,
	},

	// macOS
	brew: {
		label: 'Homebrew',
		icon: 'logos:homebrew',
		command: (p) => `brew install ${p}`,
	},
	macports: {
		label: 'MacPorts',
		icon: 'simple-icons:macports',
		command: (p) => `sudo port install ${p}`,
	},

	// Windows
	winget: {
		label: 'winget',
		icon: 'logos:microsoft-windows-icon',
		command: (p) => `winget install ${p}`,
	},
	scoop: {
		label: 'Scoop',
		icon: 'emojione:ice-cream',
		command: (p) => `scoop install ${p}`,
	},
	choco: {
		label: 'Chocolatey',
		icon: 'simple-icons:chocolatey',
		command: (p) => `choco install ${p}`,
	},

	// Language-specific
	cargo: {
		label: 'Cargo',
		icon: 'logos:rust',
		command: (p) => `cargo install ${p}`,
	},
	pip: {
		label: 'pip',
		icon: 'catppuccin:python',
		command: (p) => `pip install ${p}`,
	},
	gem: { label: 'gem', icon: 'logos:ruby', command: (p) => `gem install ${p}` },
	go: { label: 'go', icon: 'logos:go', command: (p) => `go install ${p}` },

	// Universal
	flatpak: {
		label: 'Flatpak',
		icon: 'simple-icons:flatpak',
		command: (p) => `flatpak install ${p}`,
	},
	snap: {
		label: 'Snap',
		icon: 'vscode-icons:file-type-snapcraft',
		command: (p) => `sudo snap install ${p}`,
	},
	mise: {
		label: 'Mise',
		icon: 'hugeicons:chef',
		command: (p) => `mise use -g ${p}`,
	},
};

const ALIASES: Record<string, string> = {
	arch: 'pacman',
	mac: 'brew',
	win: 'winget',
	windows: 'winget',
	window: 'winget',
	linux: 'apt',
	aur: 'yay',
};

type InstallProps = Record<string, string>;

/**
 * space separated args
 * @example ::install{brew="vlc" pacman="vlc-bin" winget="vlc"}
 */
export function Install(props: InstallProps) {
	const { pkg: defaultPkg, ...rest } = props;

	const entries = Object.entries(rest)
		.map(([key, value]) => {
			const resolved = ALIASES[key] ?? key;
			const installer = INSTALLERS[resolved];
			if (!installer) return null;
			const pkg = value || defaultPkg || 'package';
			return {
				label: installer.label,
				icon: installer.icon,
				command: installer.command(pkg),
			};
		})
		.filter((x): x is NonNullable<typeof x> => x != null);

	if (entries.length === 0) return null;

	return (
		<CodeTabs>
			{entries.map(({ label, icon, command }) => (
				<pre key={label}>
					<code data-filename={label} data-icon={icon} data-language="bash">
						{command}
					</code>
				</pre>
			))}
		</CodeTabs>
	);
}

function splitPkg(pkg?: string) {
	const parts = pkg?.split('|');
	const get = (i: number) => parts?.[i] ?? parts?.[0] ?? 'package';
	return get;
}

export function NpmInstall({ pkg }: { pkg?: string }) {
	const get = splitPkg(pkg);
	return <Install npm={get(0)} pnpm={get(1)} bun={get(2)} yarn={get(3)} />;
}

export function LinuxInstall({ pkg }: { pkg?: string }) {
	const get = splitPkg(pkg);
	return <Install pacman={get(0)} apt={get(1)} dnf={get(2)} zypper={get(3)} />;
}

export function ArchInstall({ pkg }: { pkg?: string }) {
	const get = splitPkg(pkg);
	return <Install pacman={get(0)} yay={get(1)} />;
}
